import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import "./TripAI.css";
import { countries } from "../../Data/Countries";
import { overlapError, tripLengthError, cleanDestination } from "../../Common/DestinationValidation";

// Event.Start/End are inconsistently either a Luxon DateTime (events loaded
// from a saved guide, via Event.js's json_to_appointment) or a plain
// UTC-tagged ISO string (events just built in-memory, e.g. by
// BuildMyTripWizard.js's addEventOpeningHoursAware, which sets event.Start via
// .toISO()) — normalize to a DateTime here so every date op below
// (.plus/.startOf/.toFormat/comparisons) works regardless of which path
// produced the event.
const asLuxon = (value) => (DateTime.isDateTime(value) ? value : DateTime.fromISO(value, { zone: "utc" }));

// Every default (non-explicit-sourceEvents) call site scopes derivation to
// just the "Main" schedule's events, not the whole guide — a guide can have
// other schedules holding events that were never meant to be trip
// destinations at all, e.g. a hidden Wanderlist-statistics place tracked in
// its own separate schedule. Missing Itinerary means "Main" (same convention
// as TripAI.js's _deleteMainScheduleDaysInRange). Future: let the user pick
// which schedule to derive from instead of hardcoding "Main".
export const mainScheduleEvents = () =>
  (window.guide_obj?.schedule?.events || []).filter((e) => (e.Itinerary || "Main") === "Main");

// THE single canonical answer to "what are this guide's destinations right
// now" — DestinationsScreen.js, TripAI.js, and BuildMyTripWizard.js all call
// this instead of each independently reimplementing the same
// persisted → derived-from-Main fallback chain (three separate copies of
// this logic is exactly how they drifted out of sync with each other
// before). Returns only what's persisted on Guide.destinations, else [].
// It deliberately does NOT auto-derive from the schedule (that produced bad
// destination data); the user can still run "Destinations From Schedule"
// explicitly from DestinationsScreen.js.
export const effectiveDestinations = () => {
  const saved = window.guide_obj?.destinations || [];
  return saved.map(cleanDestination);
};

// "Destinations From Schedule" — extracted from TripAI.js's original inline
// _deriveDestinationsFromSchedule/_deriveDestinationsFromEvents/
// _applyScheduleDestinations, same deliberate-extraction pattern as
// ExploreDestinationsFlow.js. TripAI.js's inline copy was removed once it
// became a read-only consumer of Guide.destinations (see DestinationsScreen.js).
//
// Reads mainScheduleEvents() (or an explicit sourceEvents array, for a caller
// that wants to scope this to e.g. just-imported events rather than the
// whole Main schedule) and groups consecutive same-city/country events into
// destination candidates the user can review before adding them to Build My
// Trip's own destinations list — or, for an event with no resolvable place,
// consecutive same-Subject events into a noPlace/placeholder candidate.
//
// Props: sourceEvents (optional array — defaults to the Main schedule),
// onApply(destinations), onCancel().
export const deriveCandidatesFromEvents = (sourceEvents) => {
  const events = (sourceEvents || [])
    .filter((e) => e.Start)
    .map((e) => ({ ...e, Start: asLuxon(e.Start), End: e.End ? asLuxon(e.End) : e.End }))
    .sort((a, b) => a.Start - b.Start);

  // One destination candidate per consecutive run of events sharing the
  // same City/Country — deliberately City+Country, not the specific Place
  // record, so consecutive-day stays consolidate into one entry even when
  // they're technically different Place entries in that city. A gap (a
  // different city's event in between) still starts a new run.
  //
  // An event with no resolvable place (e.g. a cruise's "Sea Day"/a pure
  // travel day — has a Start but no Places, or a Places id that doesn't
  // resolve) becomes a placeholder/noPlace candidate instead of being
  // silently dropped (see DestinationsScreen.js's noPlace destinations) —
  // grouped by consecutive same-Subject runs the same way real places group
  // by city, using the event's Subject as the label.
  const runs = [];
  let currentRun = null;
  events.forEach((e) => {
    const placeId = String(e.Places || "").split(",")[0].trim();
    const place = placeId ? window.guide_obj.places.getPlace(placeId) : null;
    let runKey, city, country;
    if (place) {
      city = place.City || place.Name;
      country = place.Country || "";
      runKey = `place|${city.toLowerCase()}||${country.toLowerCase()}`;
    } else {
      city = "";
      country = (e.Subject || "Travel Day").trim();
      runKey = `noplace|${country.toLowerCase()}`;
    }
    if (currentRun && currentRun.runKey === runKey) {
      currentRun.events.push(e);
    } else {
      currentRun = { runKey, city, country, firstPlace: place, hasPlace: !!place, events: [e] };
      runs.push(currentRun);
    }
  });

  const candidates = runs.map((run, i) => {
    const { city, country, firstPlace, hasPlace, events: runEvents } = run;
    const first = runEvents[0];
    const last = runEvents[runEvents.length - 1];

    if (!hasPlace) {
      // No arrival/departure semantics to infer for a placeholder day (that
      // treatment below is specifically for a real place's transportation
      // timing) — just the run's own Start, and its End if it has one real
      // (a merged/multi-event run), else a modest default block.
      const dateStart = first.Start;
      const dateEnd = last.End && last.End > dateStart ? last.End : dateStart.plus({ minutes: 30 * runEvents.length });
      return {
        id: `sched_${i}`,
        city: "",
        country,
        countryResolved: true,
        dateStart,
        dateEnd,
        selected: true,
        conflict: false,
        hasTransportTimes: true,
      };
    }

    let dateStart = first.Start;
    let dateEnd;
    const isSingleEvent = runEvents.length === 1;
    if (!isSingleEvent) {
      // With 2+ distinct events, the run's LAST one marks departure — its
      // own Start is the real parsed timestamp; its End is just that
      // event's default duration (a display-block length, not a second
      // real value), so using it silently inflated the destination's
      // departure time by an arbitrary default (confirmed: activities were
      // landing ~30min PAST the real ship departure instead of stopping
      // before it, because the "before" padding below was computed against
      // an already-too-late dateEnd).
      dateEnd = last.Start;
    } else {
      // A single event's End DOES hold a real second timestamp when it's a
      // merged arrival+departure unit (see ImportConfirmationModal's
      // "Combine arrival & departure" option) — fall back to the place's
      // own typical visit duration only when there's truly nothing else to
      // go on. Overridden below for the trip's first/last destination,
      // where a lone event is a one-sided departure or arrival instead.
      dateEnd = last.End && last.End > dateStart ? last.End : null;
      if (!dateEnd) dateEnd = dateStart.plus({ minutes: firstPlace.Duration || 30 });
    }

    if (i === 0 && isSingleEvent) {
      // The trip's very first destination with only a single event (e.g. a
      // cruise's embarkation port) states a departure/boarding time, not a
      // real arrival — use that exact timestamp as dateEnd (not padded with
      // a default event duration, which would push the cutoff later than
      // the confirmation actually said) and assume a 9am arrival instead of
      // reusing it as dateStart too, which would leave no time at all for
      // that destination's own day. The user can adjust either on the
      // destination card, e.g. for their actual flight/hotel arrangements.
      //
      // If that first event is itself AT OR BEFORE 9am (an early-morning
      // departure), the 9am default would land at or after dateEnd —
      // rejected by the "end must be after start" save validation even
      // though the data is perfectly valid. Fall back to a buffer strictly
      // before dateEnd instead of the fixed clock hour in that case. Fixed
      // 2026-08-31 via on-device testing (mobile's identical fix).
      dateEnd = first.Start;
      const defaultStart = dateEnd.startOf("day").set({ hour: 9, minute: 0 });
      dateStart = defaultStart < dateEnd ? defaultStart : dateEnd.minus({ hours: 1 });
    } else if (i === runs.length - 1 && isSingleEvent) {
      // Symmetric case at the very end of the trip (e.g. disembarkation) —
      // a single event there states an arrival, not a real departure. Same
      // treatment in reverse: exact timestamp as dateStart, assume a 6pm
      // end of day rather than padding the arrival by a default duration —
      // with the same at-or-after-6pm fallback as above.
      dateStart = first.Start;
      const defaultEnd = dateStart.startOf("day").set({ hour: 18, minute: 0 });
      dateEnd = defaultEnd > dateStart ? defaultEnd : dateStart.plus({ hours: 1 });
    }

    const matchedCountry = countries.find((c) => c.toLowerCase() === country.toLowerCase());
    return {
      id: `sched_${i}`,
      city,
      country: matchedCountry || country,
      countryResolved: !!matchedCountry,
      dateStart,
      dateEnd,
      selected: !!matchedCountry,
      conflict: false,
      // Every candidate from this flow came from real schedule events (a
      // confirmation import, a cruise/flight, etc.) — dateStart/dateEnd are
      // genuine arrival/departure moments, not just "days I'll be in this
      // city" chosen by hand or by Explore Destinations' day allocation.
      // BuildMyTripWizard.js only applies the arrival-floor/departure-padding
      // treatment (a hard point-in-time, not an activity to schedule
      // around) when this is true — a manually-entered or Explore-generated
      // destination has no such real-world transportation constraint to
      // enforce.
      hasTransportTimes: true,
    };
  });

  // A confirmation's own events for a single multi-day stay routinely get
  // interrupted mid-run by something at a DIFFERENT place (a hotel
  // check-in event, a brief transfer) — e.g. Kanazawa arrival, then a
  // hotel check-in event, then more Kanazawa events — which breaks the
  // run-grouping above into multiple separate SAME-city candidates instead
  // of one continuous stay. Each fragment is a real sub-range of the same
  // visit, so they legitimately overlap each other once built, showing as
  // false "overlaps another" on every one of them (confirmed on a real
  // 16-day itinerary — several cities each appeared 2-3 times with
  // overlapping ranges). Merging same-city-and-country candidates whose
  // ranges overlap or sit within a day of each other into one spanning
  // range fixes this at the source instead of just flagging the symptom.
  // A gap over a day means a genuine separate later revisit, which stays
  // distinct. Fixed 2026-08-30 via on-device testing (mobile's
  // DestinationsScreen.js has the identical fix).
  const mergeSameCityCandidates = (unmerged) => {
    const groups = new Map();
    unmerged.forEach((c) => {
      const key = `${c.city.toLowerCase()}||${c.country.toLowerCase()}`;
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(c);
    });
    const MERGE_GAP = { hours: 24 };
    const mergedGroups = [];
    groups.forEach((group) => {
      const sorted = [...group].sort((a, b) => a.dateStart - b.dateStart);
      const runs = [];
      let current = null;
      sorted.forEach((c) => {
        if (current && c.dateStart <= current.dateEnd.plus(MERGE_GAP)) {
          if (c.dateEnd > current.dateEnd) current.dateEnd = c.dateEnd;
          current.selected = current.selected || c.selected;
        } else {
          current = { ...c };
          runs.push(current);
        }
      });
      mergedGroups.push(...runs);
    });
    return mergedGroups.sort((a, b) => a.dateStart - b.dateStart).map((c, i) => ({ ...c, id: `sched_${i}`, conflict: false }));
  };
  const merged = mergeSameCityCandidates(candidates);

  // merged is built from one strictly time-ordered event stream — by
  // construction, only ADJACENT candidates can ever share any time at
  // all: a same-day handoff (leave Kyoto in the morning, arrive Osaka
  // that evening) is exactly what real sequential travel looks like, not
  // a data error, so flagging every (i, i+1) pair was just noise on every
  // single candidate. A genuine conflict — two candidates that aren't
  // next to each other still overlapping — would mean something actually
  // went wrong upstream and is still worth flagging.
  for (let i = 0; i < merged.length; i++) {
    for (let j = i + 2; j < merged.length; j++) {
      if (merged[i].dateStart < merged[j].dateEnd && merged[j].dateStart < merged[i].dateEnd) {
        merged[i].conflict = true;
        merged[j].conflict = true;
        merged[i].selected = false;
        merged[j].selected = false;
      }
    }
  }

  return merged;
};

const ScheduleDestinationsFlow = ({ sourceEvents, onApply, onCancel }) => {
  const [candidates, setCandidates] = useState(() =>
    deriveCandidatesFromEvents(sourceEvents || mainScheduleEvents())
  );
  const [error, setError] = useState(() => (candidates.length === 0 ? "No places with dates found in the current schedule." : ""));

  const toggleCandidate = (id) => {
    setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, selected: !c.selected } : c)));
  };

  const applyDestinations = () => {
    const selected = candidates.filter((c) => c.selected);
    if (selected.length === 0) {
      setError("Select at least one destination.");
      return;
    }
    // No overlapError() re-check here — unlike manually-edited destinations
    // (where the generic, strictly-adjacent-inclusive check in
    // DestinationValidation.js is exactly right, since a person really
    // could fat-finger genuinely conflicting dates), these candidates
    // already went through mergeSameCityCandidates + the schedule-aware
    // conflict check above, which correctly treats a same-day handoff
    // between sequential real-travel stops as normal, not an error.
    // Re-running the strict generic check here just reintroduced the exact
    // false positive already fixed on the review screen. Matches mobile's
    // identical fix. Fixed 2026-08-30.
    const lengthError = tripLengthError(selected);
    if (lengthError) {
      setError(lengthError);
      return;
    }
    // When derived from an explicit event list (the "Import Confirmation
    // Email" case, not the general "Destinations From Schedule" button),
    // every event shares the same Itinerary. Place-less events (e.g. a
    // cruise's "Sea Day") now DO turn into their own noPlace destination
    // candidates above, but they still have no real place to search for —
    // telling the caller which schedule they came from lets it default to
    // appending there instead of a brand-new schedule, so the events those
    // candidates were derived from stay in the same schedule any new places
    // join.
    const sourceScheduleName = sourceEvents && sourceEvents.length > 0
      ? (sourceEvents[0].Itinerary || "Main").replace(/%20/g, " ")
      : null;
    onApply(selected.map(({ id, city, country, dateStart, dateEnd, hasTransportTimes }) => ({ id, city, country, dateStart, dateEnd, hasTransportTimes })), sourceScheduleName);
  };

  return (
    <div className="tai-paste-overlay" onClick={onCancel}>
      <div className="tai-paste-modal" onClick={(e) => e.stopPropagation()}>
        <p className="tai-paste-title">Destinations From Schedule</p>
        <p className="tai-paste-hint">
          Derived from the current guide's schedule. Uncheck any you don't want to include.
        </p>
        {error !== "" && <p className="tai-paste-error">{error}</p>}
        <div className="tai-schedule-candidates">
          {candidates.map((c) => (
            <div key={c.id} className={`tai-schedule-candidate-row${c.conflict ? " conflict" : ""}`}>
              <input type="checkbox" checked={c.selected} onChange={() => toggleCandidate(c.id)} />
              <span className="tai-schedule-candidate-label">
                {c.city ? `${c.city}${c.countryResolved ? `, ${c.country}` : " (unrecognized country)"}` : c.country}
              </span>
              <span className="tai-schedule-candidate-dates">
                {c.dateStart.toFormat("LLL d")} – {c.dateEnd.toFormat("LLL d, yyyy")}
              </span>
              {c.conflict && <span className="tai-schedule-candidate-conflict">overlaps another</span>}
            </div>
          ))}
        </div>
        <div className="tai-paste-actions">
          <button className="tai-cancel-btn" onClick={onCancel}>Cancel</button>
          <button className="tai-primary-btn" onClick={applyDestinations}>
            <FontAwesomeIcon icon={solid("check")} />
            Use Selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleDestinationsFlow;
